import type { MouseEventHandler } from 'react'
import { isValidElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import type { SelectionOption } from 'components/SearchV2/Components/FilterPanel/FilterPanel'
import { HolidayTypeToIconMap, getTopNFilterOptions, getHolidayTypeIcon, type GetTopNFilterItem } from 'lib/search/searchUtils'
import { connect } from 'react-redux'
import ToggleButton from 'LuxKit/ToggleButton'
import FilterChip from 'LuxKit/Chips/FilterChip'

import { useAppDispatch } from 'hooks/reduxHooks'
import { replace } from 'lib/routing/appRouting'
import { deleteSearchParams, setManySearchParamValues } from 'lib/url/searchUrlUtils'
import getOfferListKey from 'lib/offer/offerListKey'

const EMPTY_EXCLUDED_OPTIONS: Array<string> = []

interface Props {
  filters: App.OfferListFilters
  search: string
  excludedOptions?: Array<string>
  pills?: boolean
}

interface MappedStateProps {
  availableFilters?: App.OfferListFilterOptions
}

interface AnywhereFilterOptionProps {
  option: SelectionOption
  pills: boolean
  selected: boolean
  onSelect: MouseEventHandler<HTMLButtonElement>
}

function AnywhereFilterOption({ option, pills, selected, onSelect }: AnywhereFilterOptionProps) {
  if (pills) {
    return (
      <FilterChip
        size="small"
        startIcon={isValidElement(option.icon) ? option.icon : undefined}
        selected={selected}
        data-selectionid={option.text}
        onClick={onSelect}
      >
        {option.text}
      </FilterChip>
    )
  }

  return (
    <ToggleButton
      kind="primary"
      orientation="vertical"
      startIcon={option.icon}
      title={option.text}
      isSelected={selected}
      data-selectionid={option.text}
      onClick={onSelect}
    />
  )
}

function AnywhereFilterTabs(props: Props & MappedStateProps) {
  const intl = useIntl()

  // these are used in useCallback below so wrapping them in useMemo
  const sortFilters = useMemo<Record<string, { sortParam: string, iconSlug: string }>>(() => ({
    [intl.formatMessage({
      id: '8hLfe9wCAvTCXmRh1ADt',
      defaultMessage: 'Best Value',
      description: 'Filter tab label for sorting by best value in the filters panel',
    })]: { sortParam: 'value', iconSlug: 'best_value' },
  }), [intl])

  const amenityFilters = useMemo<Record<string, string>>(() => ({
    [intl.formatMessage({
      id: 'InjFy_ekR3r6QMbHDI-5',
      defaultMessage: 'Private Pool',
      description: 'Filter tab label for private pool amenity in the filters panel',
    })]: 'private_pool',
    [intl.formatMessage({
      id: '7Q4FswWNFJlJdsp9m11X',
      defaultMessage: 'Ocean Views',
      description: 'Filter tab label for ocean views amenity in the filters panel',
    })]: 'ocean_views',
  }), [intl])

  const allLabel = intl.formatMessage({
    id: 'XO57KZ6rtUkIFUyme0qQ',
    defaultMessage: 'All',
    description: 'Filter tab label for showing all results in the filters panel',
  })

  const { filters, availableFilters, search, excludedOptions = EMPTY_EXCLUDED_OPTIONS, pills = false } = props
  const [currentTab, setCurrentTab] = useState<string>(() => {
    if (filters.holidayTypes?.[0]) {
      const slug = filters.holidayTypes[0]
      const filterItem = availableFilters?.filters?.holidayTypes?.find(item => item.slug === slug)
      return filterItem?.label ?? slug
    }
    if (filters.amenities?.[0] && Object.values(amenityFilters).includes(filters.amenities[0])) {
      return Object.keys(amenityFilters).find((key) => amenityFilters[key] === filters.amenities![0]) ?? allLabel
    }
    if (filters.sortBy && Object.values(sortFilters).some(v => v.sortParam === filters.sortBy)) {
      return Object.keys(sortFilters).find((key) => sortFilters[key].sortParam === filters.sortBy) ?? allLabel
    }
    return allLabel
  })
  const [availableFiltersInState, setAvailableFiltersInState] = useState<App.OfferListFilterOptions | null>(null)
  const initialOptionsOrder = useRef<Array<string> | null>(null)

  const labelToSlug = useMemo(() => {
    const map = new Map<string, string>()
    availableFiltersInState?.filters?.holidayTypes?.forEach(item => { map.set(item.label, item.slug) })
    return map
  }, [availableFiltersInState?.filters?.holidayTypes])

  useEffect(() => {
    if (availableFilters !== undefined) {
      setAvailableFiltersInState(availableFilters)
    }
  }, [availableFilters])

  const dispatch = useAppDispatch()

  const handleSelect = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    const selectionId = String(event.currentTarget.dataset.selectionid)
    if (currentTab === selectionId || selectionId === allLabel) {
      setCurrentTab(allLabel)
      dispatch(
        replace({
          search: deleteSearchParams(
            search,
            'holidayTypes',
            'amenities',
            'sortBy',
            'limit',
          ),
        }),
      )
    } else if (Object.keys(sortFilters).includes(selectionId)) {
      setCurrentTab(selectionId)
      dispatch(
        replace({
          search: setManySearchParamValues(
            search,
            [
              { paramName: 'holidayTypes', paramValue: undefined },
              { paramName: 'amenities', paramValue: undefined },
              { paramName: 'sortBy', paramValue: sortFilters[selectionId].sortParam },
              { paramName: 'limit', paramValue: undefined },
            ],
          ),
        }),
      )
    } else if (Object.keys(amenityFilters).includes(selectionId)) {
      setCurrentTab(selectionId)
      dispatch(
        replace({
          search: setManySearchParamValues(
            search,
            [
              { paramName: 'holidayTypes', paramValue: undefined },
              { paramName: 'amenities', paramValue: amenityFilters[selectionId] },
              { paramName: 'sortBy', paramValue: undefined },
              { paramName: 'limit', paramValue: undefined },
            ],
          ),
        }),
      )
    } else {
      setCurrentTab(selectionId)
      const slug = labelToSlug.get(selectionId) ?? selectionId
      dispatch(
        replace({
          search: setManySearchParamValues(
            search,
            [
              { paramName: 'holidayTypes', paramValue: slug },
              { paramName: 'amenities', paramValue: undefined },
              { paramName: 'sortBy', paramValue: undefined },
              { paramName: 'limit', paramValue: undefined },
            ],
          ),
        }),
      )
    }
  }, [currentTab, dispatch, search, allLabel, sortFilters, amenityFilters, labelToSlug])

  const availableHolidayTypeFilters = availableFiltersInState?.filters?.holidayTypes

  const topHolidayTypes = getTopNFilterOptions(availableHolidayTypeFilters, availableFiltersInState?.filterOrder?.holidayTypes, 30)

  if (!topHolidayTypes) {
    return null
  }

  const customerFilters: Array<GetTopNFilterItem> = [
    ...Object.entries(amenityFilters).map(([label, slug]) => ([slug, 100, 10, label] as GetTopNFilterItem)),
    ...Object.entries(sortFilters).map(([label, { iconSlug }]) => ([iconSlug, 100, 10, label] as GetTopNFilterItem)),
  ]

  const allFilters = [
    ...topHolidayTypes.slice(0, 6),
    ...customerFilters,
    ...topHolidayTypes.slice(6),
  ]

  const topHolidayTypeOptions = allFilters.map<SelectionOption>(
    ([slug, , , label]) => {
      return {
        icon: getHolidayTypeIcon(slug),
        text: label,
        id: `${slug}-theme`,
      }
    },
  )

  const themeOptions: Array<SelectionOption> = [{
    icon: HolidayTypeToIconMap.all,
    text: allLabel,
    id: 'All-theme',
  }]
  const options = themeOptions.concat(topHolidayTypeOptions)
    .filter(option => !excludedOptions.includes(option.text))

  if (!initialOptionsOrder.current) {
    initialOptionsOrder.current = options.map(option => option.text)
  }
  const orderIndex = (option: SelectionOption, index: number) => {
    const orderedIndex = initialOptionsOrder.current?.indexOf(option.text) ?? -1
    return orderedIndex === -1 ? options.length + index : orderedIndex
  }
  const orderedOptions = [...options].sort((optionA, optionB) => (
    orderIndex(optionA, options.indexOf(optionA)) - orderIndex(optionB, options.indexOf(optionB))
  ))

  return (
    <CardCarousel gap={pills ? 8 : 0}>
      {orderedOptions.map((option) => (
        <AnywhereFilterOption
          key={option.id}
          option={option}
          pills={pills}
          selected={currentTab === option.text}
          onSelect={handleSelect}
        />
      ))}
    </CardCarousel>
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>((appState, ownProps) => {
  const offerListKey = getOfferListKey(ownProps.filters)
  return {
    availableFilters: appState.offer.offerListFilterOptions[offerListKey],
  }
})(AnywhereFilterTabs)
